import { IComponentForTree, IOrganizationForTree, IProgramForTree, IProgramFunctionForTree, ISelectedData, ITreeItem, ITreeItemES } from "../interfaces/tree";
import { EtyEmployer } from "../interfaces/entities";
import { TLogicalOperator } from "../interfaces/types";

interface ISetItemsRecursiveArgs {
    type: 'program' | 'component' | 'function' | 'organization' | 'employer';
    items: IProgramForTree[] | IComponentForTree[] | IProgramFunctionForTree[] | IOrganizationForTree[] | EtyEmployer[];
    onClickItem: (item: ITreeItem) => void;
    selected: ISelectedData | null;
    expanded: ITreeItemES[];
    parentMethod: TLogicalOperator | null;
    lastLevel: number;
};
export const setItemsRecursive = (args: ISetItemsRecursiveArgs): ITreeItem[] => {
    const { type, items, onClickItem, selected, expanded, parentMethod, lastLevel } = args;

    const oItems: ITreeItem[] = [];

    for (const item of items) {
        const newObject: ITreeItem = {
            type: type,
            id: item.id,
            parentId: null,
            level: 0,
            onClickItem: onClickItem,
            data: item,
            name: "",
            method: 'and',
            parentMethod: parentMethod,
            children: [],
            selected: false,
            expanded: false,
            lastLevel: lastLevel,
        };

        if (type === 'program') {
            newObject.name = (item as IProgramForTree).name;
            newObject.selected = selected?.level === 0 && selected?.id === item.id;
            newObject.expanded = !!expanded.find(i => i.level === 0 && i.id === item.id);
            newObject.method = (item as IProgramForTree).method;

            if ((item as IProgramForTree).components.length > 0) {
                newObject.children = setItemsRecursive({
                    type: 'component',
                    items: (item as IProgramForTree).components,
                    onClickItem: onClickItem,
                    selected: selected,
                    expanded: expanded,
                    parentMethod: (item as IProgramForTree).method,
                    lastLevel: lastLevel,
                });
            };
        } else if (type === 'component') {
            newObject.name = (item as IComponentForTree).description;
            newObject.level = 1;
            newObject.parentId = (item as IComponentForTree).program_id;
            newObject.selected = selected?.level === 1 && selected?.id === item.id;
            newObject.expanded = !!expanded.find(i => i.level === 1 && i.id === item.id);
            newObject.method = (item as IComponentForTree).method;
            newObject.parentMethod = null;

            if ((item as IComponentForTree).program_functions.length > 0) {
                newObject.children = setItemsRecursive({
                    type: 'function',
                    items: (item as IComponentForTree).program_functions,
                    onClickItem: onClickItem,
                    selected: selected,
                    expanded: expanded,
                    parentMethod: (item as IComponentForTree).method,
                    lastLevel: lastLevel,
                });
            };
        } else if (type === 'function') {
            newObject.name = (item as IProgramFunctionForTree).name;
            newObject.level = 2;
            newObject.parentId = (item as IProgramFunctionForTree).component_id;
            newObject.selected = selected?.level === 2 && selected?.id === item.id;
            newObject.expanded = !!expanded.find(i => i.level === 2 && i.id === item.id);
            newObject.method = null;
            newObject.parentMethod = null;
        } else if (type === 'organization') {
            newObject.name = (item as IOrganizationForTree).name;
            newObject.selected = selected?.level === 0 && selected?.id === item.id;
            newObject.expanded = !!expanded.find(i => i.level === 0 && i.id === item.id);
            newObject.method = null;
            if ((item as IOrganizationForTree).employers.length > 0) {
                newObject.children = setItemsRecursive({
                    type: 'employer',
                    items: (item as IOrganizationForTree).employers,
                    onClickItem: onClickItem,
                    selected: selected,
                    expanded: expanded,
                    parentMethod: null,
                    lastLevel: lastLevel,
                });
            };
        } else if (type === 'employer') {
            newObject.name = (item as EtyEmployer).legal_name;
            newObject.level = 1;
            newObject.selected = selected?.level === 1 && selected?.id === item.id;
            newObject.expanded = !!expanded.find(i => i.level === 1 && i.id === item.id);
            newObject.method = null;
        }

        oItems.push(newObject)
    };

    return oItems;
};

export const findItemRecursive = (items: ITreeItem[], id: number, level: number): ITreeItem | undefined => {
    let item;
    for (const element of items) {
        if (element.id === id && element.level === level) {
            item = element;
            break;
        } else if (element.children.length > 0) {
            item = findItemRecursive(element.children, id, level);
            if (item) {
                break;
            };
        }
    };
    return item;
};

export const resetSelected = (items: ITreeItem[]): ITreeItem[] => {
    const newItems = [...items];

    newItems.forEach(item => {
        item.selected = false;

        if (item.children.length > 0) {
            resetSelected(item.children);
        };
    });

    return newItems;
};

export const removeTemporaryItems = (items: ITreeItem[]): ITreeItem[] => {
    const newItems = items.filter(item => item.id > 0);

    newItems.forEach(item => {
        if (item.children.length > 0) {
            item.children = removeTemporaryItems(item.children);
        };
    });

    return newItems;
};